import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router';
import { Button, Input, Modal, theme as antdTheme, type InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { chrome } from '@viq-admin/theme';
import { space, useCompactHeader, useNarrowViewport } from '@viq-admin/ui';
import { clearRecent, useRecentItems } from '../recent/recentItems';
import {
  flattenGroups,
  recentGroup,
  searchConsole,
  type SearchResult,
} from './searchModel';
import { SearchResults } from './SearchResults';
import { useSearchSources } from './useSearchSources';

/**
 * The console's header search: one box that jumps to any company, user or
 * conference (or a lead, by pasted id). The daily super admin's quickest
 * route to a record, so it is always in the header and always one keystroke
 * away: `/` (when not already typing somewhere) or ⌘K / Ctrl+K (anywhere).
 *
 * Desktop: a ~320px input whose results drop down beneath it.
 * Phone and tablet (below the compact-header breakpoint, 992px): a search icon
 * in the header that opens the same search full-screen. A dropdown under a
 * 375px header has nowhere to go, and at tablet width an inline box crowds the
 * header until both it and the brand are squeezed.
 *
 * Matching and ranking live in `searchModel.ts`; the lists come from
 * `useSearchSources`, which only starts fetching once the search is first
 * opened. With an empty box the list shows "Recently opened" records (kept by
 * the detail pages via `useTrackRecent`), with Clear.
 *
 * Keyboard: ↑/↓ move, Enter opens (the top result is pre-selected while
 * typing, so type-then-Enter is the common path), Esc closes. The mouse works
 * the same way. Reads only: no access check beyond each page's own gate.
 */
export function HeaderSearch() {
  const compact = useCompactHeader();
  const narrow = useNarrowViewport();
  return compact || narrow ? <PhoneSearch /> : <DesktopSearch />;
}

const PLACEHOLDER = 'Search companies, users, conferences';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}

/**
 * `/` and ⌘K / Ctrl+K open the search from anywhere in the console. `/` is
 * left alone while the operator is typing in a field, or inside an open
 * dialog, so it still types a slash there; ⌘K / Ctrl+K always wins.
 */
function useSearchShortcut(onTrigger: () => void) {
  const latest = useRef(onTrigger);
  latest.current = onTrigger;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        latest.current();
        return;
      }
      if (
        e.key === '/' &&
        !mod &&
        !e.altKey &&
        !isTypingTarget(e.target) &&
        !(e.target instanceof Element && e.target.closest('[role="dialog"]'))
      ) {
        e.preventDefault();
        latest.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}

/**
 * State shared by both layouts: the query, the highlighted option, the
 * results, and what Enter / click / Esc do. `onClose` lets each layout put its
 * own surface away (blur the input, close the modal).
 */
function useSearchController(onClose: () => void) {
  const navigate = useNavigate();
  const [query, setQueryState] = useState('');
  // -1 = nothing highlighted. Typing pre-selects the top result; the recents
  // list starts with nothing selected, so Enter on an empty box does nothing.
  const [activeIndex, setActiveIndex] = useState(-1);
  // Flips on at first open and stays on: from then the lookups stay cached.
  const [activated, setActivated] = useState(false);
  const recents = useRecentItems();
  const { sources, loading } = useSearchSources(activated);

  const trimmed = query.trim();
  const groups = useMemo(
    () => (trimmed ? searchConsole(trimmed, sources) : recentGroup(recents)),
    [trimmed, sources, recents],
  );
  const flat = useMemo(() => flattenGroups(groups), [groups]);
  const active = activeIndex < flat.length ? activeIndex : flat.length - 1;

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = useCallback((i: number) => `${baseId}-option-${i}`, [baseId]);

  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setActiveIndex(next.trim() ? 0 : -1);
  }, []);

  const pick = useCallback(
    (result: SearchResult) => {
      setQuery('');
      onClose();
      navigate(result.href);
    },
    [navigate, onClose, setQuery],
  );

  /** Arrow keys, Enter and Esc. Returns true when it handled the key. */
  const handleKey = (e: ReactKeyboardEvent): boolean => {
    const n = flat.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (n) setActiveIndex(active < 0 ? 0 : (active + 1) % n);
        return true;
      case 'ArrowUp':
        e.preventDefault();
        if (n) setActiveIndex(active <= 0 ? n - 1 : active - 1);
        return true;
      case 'Enter':
        if (active >= 0 && flat[active]) {
          e.preventDefault();
          pick(flat[active]);
        }
        return true;
      case 'Escape':
        e.preventDefault();
        onClose();
        return true;
      default:
        return false;
    }
  };

  return {
    query,
    trimmed,
    setQuery,
    groups,
    active,
    setActiveIndex,
    loading,
    activate: () => setActivated(true),
    pick,
    handleKey,
    listboxId,
    optionId,
  };
}

type Controller = ReturnType<typeof useSearchController>;

/** The input's combobox wiring, identical in both layouts. */
function comboboxProps(ctl: Controller, expanded: boolean) {
  return {
    role: 'combobox',
    'aria-label': PLACEHOLDER,
    'aria-expanded': expanded,
    'aria-controls': ctl.listboxId,
    'aria-autocomplete': 'list' as const,
    'aria-activedescendant': expanded && ctl.active >= 0 ? ctl.optionId(ctl.active) : undefined,
    autoComplete: 'off',
    spellCheck: false,
  };
}

function results(ctl: Controller) {
  return (
    <SearchResults
      groups={ctl.groups}
      activeIndex={ctl.active}
      listboxId={ctl.listboxId}
      optionId={ctl.optionId}
      query={ctl.trimmed}
      loading={ctl.loading}
      onPick={ctl.pick}
      onHover={ctl.setActiveIndex}
      onClearRecent={clearRecent}
    />
  );
}

function DesktopSearch() {
  const { token } = antdTheme.useToken();
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    inputRef.current?.blur();
  }, []);
  const ctl = useSearchController(close);

  useSearchShortcut(() => {
    inputRef.current?.focus({ cursor: 'all' });
    setOpen(true);
  });

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        // Gives way first when the header runs short (a portrait tablet).
        flex: '0 1 320px',
        minWidth: 120,
        lineHeight: token.lineHeight,
      }}
    >
      <Input
        ref={inputRef}
        {...comboboxProps(ctl, open)}
        placeholder={PLACEHOLDER}
        prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
        suffix={
          !focused && !ctl.query ? (
            <kbd
              title="Press / to search"
              style={{
                fontFamily: 'inherit',
                fontSize: token.fontSizeSM,
                lineHeight: 1.4,
                color: token.colorTextTertiary,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadiusXS,
                padding: '0 6px',
              }}
            >
              /
            </kbd>
          ) : undefined
        }
        allowClear
        value={ctl.query}
        onChange={(e) => {
          ctl.setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          ctl.activate();
          setOpen(true);
        }}
        onBlur={() => {
          setFocused(false);
          setOpen(false);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) setOpen(true);
          ctl.handleKey(e);
        }}
      />
      {open && (
        <div
          // Clicks inside must not blur the input, which would close this.
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            top: `calc(100% + ${space.xs}px)`,
            left: 0,
            width: 'max(100%, 440px)',
            maxWidth: `calc(100vw - ${space.xl * 2}px)`,
            maxHeight: 'min(70vh, 560px)',
            overflowY: 'auto',
            padding: space.xs,
            background: token.colorBgElevated,
            color: token.colorText,
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadowSecondary,
            zIndex: token.zIndexPopupBase,
          }}
        >
          {results(ctl)}
          <div
            style={{
              borderTop: `1px solid ${token.colorSplit}`,
              marginTop: space.xs,
              padding: `${space.xs}px ${space.sm}px 0`,
              fontSize: token.fontSizeSM,
              color: token.colorTextTertiary,
            }}
          >
            ↑ ↓ to move · Enter to open · Esc to close
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneSearch() {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ctl = useSearchController(close);
  const show = () => {
    ctl.activate();
    setOpen(true);
  };
  useSearchShortcut(show);

  return (
    <>
      <Button
        type="text"
        icon={<SearchOutlined />}
        aria-label="Search"
        aria-haspopup="dialog"
        onClick={show}
        style={{ color: chrome.onNavy, flexShrink: 0 }}
      />
      <Modal
        open={open}
        onCancel={close}
        title="Search"
        footer={null}
        destroyOnHidden
        // Full screen: the whole phone is the search, with the keyboard up.
        width="100%"
        style={{ top: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0 }}
        styles={{
          content: { minHeight: '100dvh', borderRadius: 0, padding: space.lg },
          body: { display: 'flex', flexDirection: 'column', gap: space.sm },
        }}
        afterOpenChange={(visible) => {
          if (visible) inputRef.current?.focus();
        }}
      >
        <Input
          ref={inputRef}
          {...comboboxProps(ctl, true)}
          size="large"
          placeholder={PLACEHOLDER}
          prefix={<SearchOutlined />}
          allowClear
          autoFocus
          value={ctl.query}
          onChange={(e) => ctl.setQuery(e.target.value)}
          onKeyDown={(e) => ctl.handleKey(e)}
        />
        {results(ctl)}
      </Modal>
    </>
  );
}
